import React from 'react';
import PropTypes from 'prop-types';
import Form from 'react-bootstrap/Form';
import { MdAdd, MdDeleteOutline } from 'react-icons/md';
import CustomTypeahead from '../CustomTypeahead';
import { Button, controlClass } from '../EntityList';
import { payersTotal, payerShareAmount } from '../../utils/billPayers';
import { useLabels } from '../../hooks/useLabels';
import { showCurrency } from '../../utils';

/**
 * Split a bill across payer accounts (SHOW_BILL_PAYERS): rows of
 * { account, percentage }, total capped at 100 — the remainder stays with the
 * asset owner. Each share becomes a debt on the payer's account server-side.
 * Controlled: `payers` in, `onChange(nextPayers)` out (percentage kept as
 * typed text so partial input like "33." is not clobbered).
 */
export default function BillPayersEditor({ payers, onChange, accounts, excludeAccount, amount, currency, disabled, error }) {
	const { t } = useLabels();
	const total = payersTotal(payers);
	const remainder = Math.max(0, Math.round((100 - total) * 100) / 100);
	const numericAmount = Number(amount) || 0;

	const update = (index, patch) => onChange(payers.map((p, i) => (i === index ? { ...p, ...patch } : p)));
	const remove = (index) => onChange(payers.filter((_, i) => i !== index));
	const add = () => onChange([...payers, { account: null, percentage: '' }]);

	const usedAccounts = new Set(payers.map((p) => p.account).filter(Boolean));
	const optionsFor = (current) =>
		accounts.filter((a) => a._id !== excludeAccount && (a._id === current || !usedAccounts.has(a._id)));

	return (
		<div className='tw:space-y-3'>
			{payers.length === 0 && (
				<p className='tw:m-0 tw:text-[12.5px] tw:text-ink-2'>{t('billPayers.empty')}</p>
			)}
			{payers.map((payer, index) => {
				const selected = accounts.find((a) => a._id === payer.account);
				const share = payerShareAmount({ amount: numericAmount }, { percentage: Number(payer.percentage) || 0 });
				return (
					<div key={index} className='tw:grid tw:grid-cols-1 tw:items-center tw:gap-2 md:tw:grid-cols-[1fr_120px_140px_auto]'>
						<CustomTypeahead
							id={`bill-payer-${index}`}
							labelKey={(option) => option?.name || option?.customId || ''}
							onChange={(sel) => update(index, { account: sel && sel.length ? sel[0]._id : null })}
							options={optionsFor(payer.account)}
							selected={selected ? [selected] : []}
							placeholder={t('billPayers.selectAccount')}
							disabled={disabled}
							variant='entity'
						/>
						<div className='tw:flex tw:items-center tw:gap-1.5'>
							<Form.Control
								aria-label={t('billPayers.percentage')}
								type='number'
								min={0.01}
								max={100}
								step='any'
								className={controlClass}
								value={payer.percentage}
								onChange={(e) => update(index, { percentage: e.target.value })}
								disabled={disabled}
							/>
							<span className='tw:text-[13px] tw:text-ink-2'>%</span>
						</div>
						<span className='tw:font-mono tw:text-[12.5px] tw:text-ink-1'>
							{numericAmount ? `${showCurrency(currency) || ''} ${share.toLocaleString('es-UY')}` : '—'}
						</span>
						<Button size='sm' variant='ghost' icon={<MdDeleteOutline />} onClick={() => remove(index)} disabled={disabled} aria-label={t('action.delete')} />
					</div>
				);
			})}
			<div className='tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2'>
				<Button size='sm' icon={<MdAdd />} onClick={add} disabled={disabled || total >= 100}>
					{t('billPayers.add')}
				</Button>
				{payers.length > 0 && (
					<span className={`tw:text-[12.5px] ${total > 100 ? 'tw:text-danger' : 'tw:text-ink-2'}`}>
						{t('billPayers.total')}: {total}% · {t('billPayers.ownerKeeps')}: {remainder}%
					</span>
				)}
			</div>
			{error && <p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-danger'>{error}</p>}
		</div>
	);
}

BillPayersEditor.propTypes = {
	payers: PropTypes.arrayOf(PropTypes.shape({ account: PropTypes.string, percentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) })).isRequired,
	onChange: PropTypes.func.isRequired,
	accounts: PropTypes.array.isRequired,
	excludeAccount: PropTypes.string,
	amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	currency: PropTypes.string,
	disabled: PropTypes.bool,
	error: PropTypes.string,
};
